'use client';

import { addToCart } from '@/api/cart';
import { fetchCoatingsShow, fetchLensShow } from '@/api/product';
import { IGoodsDetail, IPrice } from '@/api/types/public';
import { COATING_COMPONENT, LENS_TYPE_COMPONENT } from '@/app/_lens-components/constants';
import ClientLeft from '@/app/_lens-components/layout/ClientLeft';
import Footer from '@/app/_lens-components/layout/Footer';
import Header from '@/app/_lens-components/layout/Header';
import Coating from '@/app/_lens-components/steps/Coating';
import LensType from '@/app/_lens-components/steps/LensType';
import { useLensProcessStore } from '@/app/_lens-components/store/lens-process-store';
import { processClassName } from '@/app/_lens-components/style/responsive';
import { BaseCartRequestData, CoatingItem, LensShowResponse } from '@/app/_lens-components/types';
import {
  handleCoatingPriceInfo,
  handleColorItemPriceInfo,
  handleGoodsFramePriceInfo,
  handlerPrescriptionData,
} from '@/app/_lens-components/utils';
import { IPrescriptionOptions } from '@/components/PrescriptionFormV2/types';
import { getEmptyPrescription } from '@/components/PrescriptionFormV2/utils';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EPrescriptionType } from '@/utils/enums/goods';
import { add } from '@/utils/math';
import { randomString } from '@/utils/tools';
import { useMediaQuery } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useWhenCurUserInfoReady } from '@/hooks/useWhenReady';

export default function ClientPage({
  goodsDetail,
  isRedeem,
}: {
  goodsDetail: IGoodsDetail;
  isRedeem: boolean;
}) {
  const router = useRouter();
  const t = useTranslations('lens-process');
  // 配镜流程状态
  const addToCartText = isRedeem ? t('checkout') : t('addToCart');
  // 是否是 frame 配镜步骤
  const isFrameProcess = true;
  const { trackEvent } = useReporting();
  const isWap = useMediaQuery('(max-width:1280px)');
  const setIsGlobalLoading = useStore((state) => state.setIsGlobalLoading);
  const {
    setIsNonPrescriptionType,
    // 商品基本信息
    lensGoodsItem,
    setLensGoodsItem,
    // 是否是积分兑换商品
    setIsRedeem,
    prescriptionType,
    prescription,
    // 价格相关
    setHasIndexPrice,
    // goodsFramePriceInfo,
    setGoodsFramePriceInfo,
    // totalPrice,
    setTotalPrice,
    // lensTypePriceInfo,
    setLensTypePriceInfo,
    // coatingPriceInfo,
    setCoatingPriceInfo,
    // 选中项
    // frame only 流程整体使用这个选中项。只在加购时从这里面获取镜片id传给接口
    selectedColorItem,
    setSelectedColorItem,
    selectedCoating,
    setSelectedCoating,
    selectedLensType,
    setSelectedLensType,
    selectedBrand,
    setSelectedBrand,
    // 埋点方法
    SDLensTypePageNextClick,
    SDAddToCartBtnClick,
    SDAddToCartResult,
    sendPoints,
    setTtqEventId,
    setTrackEventFn,
    SDSendLeaveEvent,
    setCurrency,
    SDEditLensPageView,
    SDEditLensPageClose,
    setCampaignTypeIdList,
    setLensGoodsSensorsData,
    // UI状态
    setBtnDisable,
    setStepBtnText,
    setIsLoadingLensShow,
    nowStep,
    setNowStep,
    nowComponent,
    setNowComponent,
    // 重置store
    resetStore,
    setBrandExpand,
  } = useLensProcessStore();
  // 设置埋点方法到store
  useEffect(() => {
    if (trackEvent) {
      setTrackEventFn(trackEvent);
    }
  }, [trackEvent]);

  const [allComponent, setAllComponent] = useState<string[]>([
    LENS_TYPE_COMPONENT,
    COATING_COMPONENT,
  ]); // 所有组件
  const userInfo = useStore((state) => state.userInfo);
  const visitorId = useStore((state) => state.visitorId);
  const currency = useStore((state) => state.currency);
  // 配镜数据
  const [lensTypeColorData, setLensTypeColorData] = useState<LensShowResponse>([]);
  const [coatingList, setCoatingList] = useState<CoatingItem[]>([]);

  useWhenCurUserInfoReady(() => {
    setIsRedeem(!!isRedeem);
    setCurrency(currency);
    setHasIndexPrice(true);

    let entrance_edit_lens_scene = '';
    let edit_lens_type = '';
    logger.warn('sessionStorage', sessionStorage.getItem('zl-sensors-lens-entrance-scene'));
    if (sessionStorage.getItem('zl-sensors-lens-entrance-scene')) {
      const zlSensorsLensEntranceScene = sessionStorage.getItem('zl-sensors-lens-entrance-scene');
      sessionStorage.removeItem('zl-sensors-lens-entrance-scene');
      const arr = zlSensorsLensEntranceScene.split('|');
      entrance_edit_lens_scene = arr[0] || '';
      edit_lens_type = arr[1] || '';
    }
    if (goodsDetail) {
      // 创建一个简化版的LensGoodsItem对象
      const innerLensGoodsDetail = {
        product_id: goodsDetail.product.id,
        product: {
          ...goodsDetail.product,
          // 确保exclusion字段符合要求
          exclusion: goodsDetail.product.exclusion
            ? JSON.stringify(goodsDetail.product.exclusion)
            : undefined,
          // 添加LensGoodsItem特有的字段
          price_info_pc_v0: goodsDetail.product.price_info_pc_v0,
          price_info_wap_v0: goodsDetail.product.price_info_wap_v0,
          price_info_pc_v1: goodsDetail.product.price_info_pc_v1,
          price_info_wap_v1: goodsDetail.product.price_info_wap_v1,
          price_info_pc_v2: goodsDetail.product.price_info_pc_v2,
          price_info_wap_v2: goodsDetail.product.price_info_wap_v2,
          price_info_pc_v3: goodsDetail.product.price_info_pc_v3,
          price_info_wap_v3: goodsDetail.product.price_info_wap_v3,
          img: goodsDetail.product.image?.product?.[0] || '',
          color: goodsDetail.product.product_frame_color_name,
        },
      }; // 使用类型断言
      setLensGoodsItem(innerLensGoodsDetail);
      const campaignIds = goodsDetail.product?.buried_point_data?.campaign_type_id_list || [];
      setCampaignTypeIdList(campaignIds);
      const framePriceForSensor = handleGoodsFramePriceInfo(
        innerLensGoodsDetail.product,
        !!isRedeem,
        currency,
        userInfo,
      );
      setLensGoodsSensorsData({
        product_view_uuid: randomString() || '',
        shop_sku: goodsDetail.product.shop_sku,
        business_sku: goodsDetail.product.business_sku,
        product_sku: goodsDetail.product.sku,
        product_id: goodsDetail.product.id.toString(),
        product_name: goodsDetail.product.short_name,
        entrance_edit_lens_scene,
        edit_lens_type,
        product_class_name: goodsDetail.product.products_category,
        frame_price: parseFloat(framePriceForSensor.price ?? ''),
        current_frame_price: parseFloat(framePriceForSensor.sell_price ?? ''),
        currency: currency?.name ?? '',
      });
    }
    getLensShow(EPrescriptionType.FRAME_ONLY);
    getCoatingList();

    // 埋点
    document.addEventListener('visibilitychange', handleVisibilityChange);
    // 延迟上报，避免异步的 setState 还没生效
    setTimeout(() => {
      SDEditLensPageView();
    }, 200);
  }, []);

  // 组件卸载时执行的内容
  useEffect(() => {
    return () => {
      try {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        SDEditLensPageClose();
        logger.warn('SDEditLensPageClose-0');
        resetStore();
      } catch (error) {
        logger.error('Failed to reset resetStore:', error);
      }
    };
  }, []);

  useEffect(() => {
    if (lensGoodsItem) {
      if (userInfo?.id) {
        setTtqEventId(`${userInfo.id}${lensGoodsItem?.product?.shop_sku}`);
      } else {
        setTtqEventId(`${visitorId}${lensGoodsItem?.product?.shop_sku}`);
      }
    }
  }, [userInfo, lensGoodsItem, visitorId]);

  useEffect(() => {
    setNowComponent(allComponent[nowStep]);

    setIsNonPrescriptionType(
      [EPrescriptionType.NON_PRESCRIPTION, EPrescriptionType.SUNGLASSES_NON_PRESCRIPTION].includes(
        prescriptionType as EPrescriptionType,
      ),
    );
  }, [prescriptionType, nowStep]);

  useEffect(() => {
    if (!selectedColorItem || selectedColorItem?.is_coating === 1) {
      setAllComponent([LENS_TYPE_COMPONENT, COATING_COMPONENT]);
    } else {
      setAllComponent([LENS_TYPE_COMPONENT]);
    }
  }, [selectedColorItem]);

  useEffect(() => {
    const nextComponent = allComponent[nowStep + 1];
    if (!nextComponent) {
      setStepBtnText(addToCartText);
    } else {
      setStepBtnText(t('next'));
    }
    switch (nowComponent) {
      case LENS_TYPE_COMPONENT:
        setBtnDisable(!selectedColorItem?.lens_color);
        break;
      case COATING_COMPONENT:
        setBtnDisable(!selectedCoating);
        break;
      default:
        setBtnDisable(true);
    }
  }, [nowStep, nowComponent, selectedColorItem, selectedCoating, allComponent]);

  useEffect(() => {
    // 初始化商品信息
    if (lensGoodsItem && lensGoodsItem.product) {
      // 设置商品图片 注意购物车、详情页、积分兑换不同取值逻辑不同
      // 这里可以添加价格计算逻辑
      const goodsFramePriceInfo = handleGoodsFramePriceInfo(
        lensGoodsItem.product,
        isRedeem,
        currency,
        userInfo,
      );
      let lensTypePriceInfo: IPrice;
      let coatingPriceInfo: IPrice;
      if (selectedColorItem) {
        lensTypePriceInfo = handleColorItemPriceInfo(selectedColorItem, true, currency, userInfo);
      }
      if (selectedCoating) {
        coatingPriceInfo = handleCoatingPriceInfo(selectedCoating, currency, userInfo);
      }
      setGoodsFramePriceInfo(goodsFramePriceInfo);

      setLensTypePriceInfo(lensTypePriceInfo);
      setCoatingPriceInfo(coatingPriceInfo);
      setTotalPrice({
        currency: currency.name,
        sell_price: add(
          goodsFramePriceInfo.sell_price,
          lensTypePriceInfo?.sell_price || 0,
          coatingPriceInfo?.sell_price || 0,
        ).toFixed(2),
        price: add(
          goodsFramePriceInfo.price,
          lensTypePriceInfo?.price || 0,
          coatingPriceInfo?.price || 0,
        ).toFixed(2),
      });
    }
  }, [userInfo, currency, isRedeem, lensGoodsItem, selectedColorItem, selectedCoating]);

  // 处理返回按钮点击
  const handleLeftBack = () => {
    if (nowStep > 0) {
      setNowStep(nowStep - 1);
      initComponent(allComponent[nowStep - 1]);

      // app 这种情况不会报这个事件
      SDSendLeaveEvent('Return');
    }
  };

  // 处理关闭按钮点击
  const handleClose = () => {
    router.back();
    SDSendLeaveEvent('Close');
  };

  const initLensType = () => {
    setSelectedLensType(null);
    setSelectedBrand(null);
    setSelectedColorItem(null);
    setBrandExpand({});
  };
  const initCoating = () => {
    setSelectedCoating(null);
  };

  const initMap = {
    [LENS_TYPE_COMPONENT]: initLensType,
    [COATING_COMPONENT]: initCoating,
  };

  const initComponent = (component: string) => {
    const idx = allComponent.indexOf(component);
    const afterComponents = allComponent.slice(idx + 1);
    afterComponents.forEach((com) => {
      initMap[com]?.();
    });
  };
  const scrollToTop = () => {
    if (isWap) {
      window.scrollTo({
        top: 0,
        behavior: 'instant',
      });
    } else {
      const rightPart = document.querySelector('#right-part');
      if (rightPart) {
        rightPart.scrollTo({
          top: 0,
          behavior: 'instant',
        });
      }
    }
  };
  // 处理下一步按钮点击
  const handleNextStep = () => {
    const nextComponent = allComponent[nowStep + 1];
    if (!nextComponent) {
      handleAddToCart();
      return;
    }
    switch (nowComponent) {
      case LENS_TYPE_COMPONENT:
        if (prescriptionType === EPrescriptionType.FRAME_ONLY) {
          handleAddToCart();
        } else if (prescriptionType && selectedLensType && selectedColorItem) {
          // 走到这里说明有下一步镀膜，这里不再判断魔方配置
          setNowStep(nowStep + 1);
          const lens0 = selectedColorItem?.lens_list?.[0];
          const shouldReportLensSkuOnNext = [
            EPrescriptionType.NON_PRESCRIPTION,
            EPrescriptionType.FRAME_ONLY,
          ].includes(prescriptionType as EPrescriptionType);
          SDLensTypePageNextClick(
            shouldReportLensSkuOnNext
              ? {
                  lens_id: lens0?.id != null ? `${lens0.id}` : '',
                  lens_code: lens0?.code ?? '',
                }
              : {},
          );
          scrollToTop();
        }
        break;
      case COATING_COMPONENT:
        // 这里没有下一步 直接加购
        handleAddToCart();
        break;
      default:
        break;
    }
  };

  // 调用镜片类型接口
  const getLensShow = async (
    presType: EPrescriptionType | '' | undefined,
    currentPrescription?: IPrescriptionOptions,
  ) => {
    try {
      setIsGlobalLoading(true);
      setIsLoadingLensShow(true);
      // 使用传入的处方数据或者状态中的处方数据
      const prescriptionData = currentPrescription || prescription || getEmptyPrescription();
      let prescription_type = presType || prescriptionType;
      if (
        [EPrescriptionType.READERS, EPrescriptionType.PRESCRIPTION_READING].includes(
          prescription_type,
        )
      ) {
        prescription_type = EPrescriptionType.READING_GLASSES;
      }

      const { data, code } = await fetchLensShow({
        prescription_type,
        product_id: goodsDetail.product.id.toString(),
        od_sph: prescriptionData.od_sph,
        os_sph: prescriptionData.os_sph,
        od_cyl: prescriptionData.od_cyl,
        os_cyl: prescriptionData.os_cyl,
        os_add: prescriptionData.os_add,
        od_add: prescriptionData.od_add,
        is_prism: '2',
      });
      if (code === 200 && Array.isArray(data)) {
        const lensData = data as LensShowResponse;
        lensData.forEach((lensType) => {
          if (lensType.merge_brand === 1) {
            lensType.brand_list?.forEach((brand) => {
              if (brand.color_list?.length) {
                brand.default_lens_color = brand.color_list[0].lens_color;
              }
            });
          } else if (lensType.color_list?.length) {
            lensType.default_lens_color = lensType.color_list[0].lens_color;
          }
        });
        setLensTypeColorData(lensData);
      } else {
        setLensTypeColorData([]);
      }
    } catch (error) {
      logger.error('👀', error);
    } finally {
      setIsGlobalLoading(false);
      setIsLoadingLensShow(false);
    }
  };

  const getCoatingList = async () => {
    const { data, code } = await fetchCoatingsShow();
    if (code === 200) {
      setCoatingList(data);
    } else {
      setCoatingList([]);
    }
  };

  /**
   * @description: 加入购物车数据处理
   * @return {*}
   */
  const handleAddToCart = async () => {
    setIsGlobalLoading(true);
    const addToCartParams = {
      ...prescription,
      prescription_type: prescriptionType,
      lens_id: selectedColorItem?.lens_list?.[0]?.id || 0,
      coating_id: selectedCoating?.id || 0,
    };

    // 跟之前的线上逻辑保持一致：处方类型是太阳镜、非处方、镜框、成品老化，则把0.00和None改为空字符串。
    if (
      [
        EPrescriptionType.SUNGLASSES_NON_PRESCRIPTION,
        EPrescriptionType.NON_PRESCRIPTION,
        EPrescriptionType.FRAME_ONLY,
        EPrescriptionType.READERS,
      ].includes(prescriptionType)
    ) {
      Object.keys(addToCartParams).forEach((key) => {
        if (['0.00', 'None'].includes(addToCartParams[key])) {
          addToCartParams[key] = '';
        }
      });
    }

    const addToCartReqParams = {
      currency: currency.name,
      product_id: lensGoodsItem.product_id,
      qty: 1,
      option: addToCartParams,
    };

    SDAddToCartBtnClick();
    await addToCartFn(addToCartReqParams);
    // this.SET_PREVIOUS_PRESCRIPTION_TYPE('')
  };
  const handleSendPoints = (requestData: BaseCartRequestData) => {
    try {
      sendPoints(requestData);
    } catch (e) {
      logger.error('👀', e);
    }
  };
  /**
   * @description: 加入购物车
   * @param {*} params
   * @return {*}
   */
  const addToCartFn = async (params: BaseCartRequestData) => {
    const finalParams = handlerPrescriptionData(params);
    try {
      const res = await addToCart(finalParams);
      if (res.code === 200) {
        try {
          handleSendPoints(finalParams);
          SDAddToCartResult({
            is_success: true,
            service_code: '200',
            msg: res.msg,
          });
        } catch (e) {
          logger.error('👀', e);
        }

        sessionStorage.setItem('zl-sensors-entrance-cart-scene', '配镜流程加购');
        router.replace('/cart');
        // 跳转到购物车带特殊标记，用于app引流
        sessionStorage.setItem('toApp', '1');
      } else {
        SDAddToCartResult({
          is_success: false,
          service_code: `${res.code}`,
          msg: res.msg,
        });
      }
    } catch (err) {
      logger.error('👀', err);
      SDAddToCartResult({
        is_success: false,
        service_code: `${err.code}`,
        msg: err.msg,
      });
    } finally {
      setIsGlobalLoading(false);
    }
  };

  // 埋点相关方法
  function handleVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      SDSendLeaveEvent('Else');
    }
  }

  const [minHeight, setMinHeight] = useState(0);
  useEffect(() => {
    document.body.style.backgroundColor = '#f4f4f4';
    // 解决谷歌徽章遮挡问题
    if (window.___ratingbadge_0?.style) {
      window.___ratingbadge_0.style.opacity = '0';
    }
    setMinHeight(window.innerHeight);
    return () => {
      document.body.style.backgroundColor = '';
      if (window.___ratingbadge_0?.style) {
        window.___ratingbadge_0.style.opacity = '1';
      }
    };
  }, []);

  return (
    // 在dialog中，由于从 html到这里都设置了 height: 100% ，所以 height:100% 可以生效。
    <article className={processClassName.article} style={{ minHeight: `${minHeight}px` }}>
      <ClientLeft isFrameProcess={isFrameProcess} />
      <section
        id="right-part"
        className={`right-part ${processClassName.rightPart} h-full xl:overflow-y-auto box-border`}
      >
        <div className={processClassName.rightPartContent}>
          <div
            className={`right-box ${processClassName.rightPartBox} xl:pt-[20px]`}
            id="right-box-content"
          >
            {/* 顶部区域 */}
            <Header
              isFrameProcess={isFrameProcess}
              onLeftBack={handleLeftBack}
              onClose={handleClose}
            />

            {/* 中间区域 */}
            {nowComponent === LENS_TYPE_COMPONENT && (
              <LensType
                isFrameProcess={isFrameProcess}
                lensTypeColorData={lensTypeColorData}
                selectedLensType={selectedLensType}
                selectedBrand={selectedBrand}
                selectedColorItem={selectedColorItem}
                onSelectedLensType={setSelectedLensType}
                onSelectedBrand={setSelectedBrand}
                onSelectedColorItem={setSelectedColorItem}
              />
            )}
            {nowComponent === COATING_COMPONENT && (
              <Coating
                coatingList={coatingList}
                selectedCoating={selectedCoating}
                onSelectedCoating={setSelectedCoating}
              />
            )}
          </div>
          <div className={`right-side ${processClassName.rightPartSide}`} />
        </div>
      </section>

      {/* 底部区域 */}
      <Footer isFrameProcess={isFrameProcess} onNextStep={handleNextStep} />
    </article>
  );
}
